import { logger } from "@platform/browser-logger";
import {
  ConfirmationModal,
  DrawerContent,
  DrawerCustom,
  FormGenerator,
} from "@platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "react-router";

import { usePrompt } from "../../../../hooks";
import { CircularProgressCentered } from "../../../../components/CircularProgressCentered";
import { usePatchFormDraftMutation } from "../../../../hooks/usePatchFormDraftMutation";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import { RfpClientDetailsResponse } from "../../../../types/rfpApi";

import { DrawerFooter, DrawerFooterButtonConfig } from "./DrawerFooter";
import { DrawerHeader } from "./DrawerHeader";
import { Review } from "./Review";
import { SAVE_STATUS } from "./SaveDraftStatus";
import {
  CoverageDecisionRecord,
  FinalDecisionFormValues,
  InputType,
  ReviewSection,
} from "./types";
import {
  getFinalDecisionsCacheKey,
  useSubmitCoverageDecisionRecords,
} from "./useFinalDecisionApi";
import { INTERNAL_FIELDS, useFinalDecisionForm } from "./useFinalDecisionForm";
import { reviewDataToReviewSections } from "./utils";

interface FinalDecisionsDrawerProps {
  clientDetails?: RfpClientDetailsResponse;
  onClose: VoidFunction;
  onSubmitSuccess: (coverageDecisions: CoverageDecisionRecord[]) => void;
}

export function FinalDecisionsDrawer({
  clientDetails,
  onClose,
  onSubmitSuccess,
}: FinalDecisionsDrawerProps): React.JSX.Element {
  const location = useLocation();
  const [review, setReview] = useState<ReviewSection[] | null>(null);
  const queryClient = useQueryClient();
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const patchFormDraftMutation = usePatchFormDraftMutation();
  const submitCoverageDecisionMutation = useSubmitCoverageDecisionRecords();
  const {
    rfpId,
    isFormDataLoading,
    onSaveDraft,
    finalDecisionId,
    lastDraftSavedAt,
    getReviewData,
    hasAnyMissingFields,
    hasChangesFromSubmitted,
    ...formConfig
  } = useFinalDecisionForm({
    pathDraftMutation: patchFormDraftMutation,
  });
  const { isDirty, dirtyFields } = formConfig.formMethods.formState;
  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;
  const isFormValid = formConfig.formMethods.formState.isValid;
  const handleFormSubmit = formConfig.formMethods.handleSubmit;
  const { createToastNotification } = useToastNotificationService();
  const prevPath = (location.state as { prevPath?: string })?.prevPath || null;

  const [showPrompt, confirmNavigation, cancelNavigation] = usePrompt(
    isFormDirty,
    null,
    {},
    [],
    prevPath,
  ) as [boolean, () => void, () => void];

  if (isFormDataLoading) {
    return <CircularProgressCentered />;
  }

  const handleSubmit = (data: FinalDecisionFormValues): void => {
    submitCoverageDecisionMutation.mutate(
      {
        values: data,
        rfpId,
        finalDecisionId,
      },
      {
        onSuccess: (response) => {
          const allCoverageDecisions = [
            ...response.currentCoverages,
            ...response.newCoverages,
          ];
          onSubmitSuccess(allCoverageDecisions);

          createToastNotification({
            severity: "success",
            message: "Final Decision Successfully Submitted",
          });
        },
        onError: (error) => {
          logger.error("Error submitting final decision", error);
          createToastNotification({
            severity: "error",
            message:
              "There was an error submitting the final decision. Please try again!",
          });
        },
      },
    );
  };

  const handleSaveDraft = (): Promise<boolean> =>
    onSaveDraft().then((success) => {
      if (success) {
        queryClient.invalidateQueries({
          queryKey: getFinalDecisionsCacheKey(rfpId),
        });
      }
      return success;
    });

  const handleReviewClick = (): void => {
    handleSaveDraft().then(() => {
      const reviewData = getReviewData();
      setReview(reviewDataToReviewSections(reviewData));
    });
  };

  const hasMissingFields = review ? hasAnyMissingFields() : false;

  const handleConfirmationDialogSave = (): void => {
    handleSaveDraft().then(() => {
      setConfirmationDialogOpen(false);
      confirmNavigation?.();
      onClose();
    });
  };

  const handleClose = (): void => {
    if (isFormDirty) {
      setConfirmationDialogOpen(true);
    } else {
      onClose();
    }
  };

  const hasInternalDirtyFieldRecursive = (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.some((item) => hasInternalDirtyFieldRecursive(item));
    }
    if (value && typeof value === "object") {
      return Object.entries(value as Record<string, unknown>).some(
        ([key, childValue]) => {
          if (INTERNAL_FIELDS.has(key)) {
            if (typeof childValue === "boolean") {
              return childValue;
            }
            return hasInternalDirtyFieldRecursive(childValue);
          }
          return hasInternalDirtyFieldRecursive(childValue);
        },
      );
    }
    return false;
  };
  const hasInternalDirtyField = hasInternalDirtyFieldRecursive(dirtyFields);

  const hasNoNewChanges = !hasChangesFromSubmitted() && !hasInternalDirtyField;

  const saveStatus = patchFormDraftMutation.isPending
    ? SAVE_STATUS.SAVING
    : isFormDirty
      ? SAVE_STATUS.PENDING
      : SAVE_STATUS.IDLE;

  const footerSecondaryButtonConfig = review
    ? {
        label: "Back to Edit",
        onClick: () => setReview(null),
      }
    : {
        label: "Close",
        onClick: handleClose,
        disabled: !isFormValid || submitCoverageDecisionMutation.isPending,
      };
  const footerPrimaryButtonConfig: DrawerFooterButtonConfig = review
    ? {
        label: "Finalize and Submit",
        onClick: () => handleFormSubmit(handleSubmit)(),
        loading: submitCoverageDecisionMutation.isPending,
        disabled: hasMissingFields,
        tooltip: hasMissingFields
          ? "All required fields must be completed to submit"
          : undefined,
      }
    : {
        label: "Review",
        onClick: handleReviewClick,
        disabled: !isFormValid || hasNoNewChanges,
        tooltip: hasNoNewChanges
          ? "No changes have been made since final decisions have been submitted."
          : undefined,
      };

  return (
    <>
      <DrawerCustom anchor="right" size="lg" open>
        <DrawerHeader
          clientName={clientDetails?.name || ""}
          hasChanges={hasChangesFromSubmitted()}
        />
        <DrawerContent>
          {review ? (
            review.map((section) => <Review key={section.title} {...section} />)
          ) : (
            <FormGenerator<InputType, FinalDecisionFormValues>
              {...formConfig}
              onSubmit={handleSubmit}
            />
          )}
        </DrawerContent>
        <DrawerFooter
          saveStatus={saveStatus}
          onSave={handleSaveDraft}
          lastUpdated={
            lastDraftSavedAt ? new Date(lastDraftSavedAt) : undefined
          }
          primaryButtonConfig={footerPrimaryButtonConfig}
          secondaryButtonConfig={footerSecondaryButtonConfig}
        />
      </DrawerCustom>

      <ConfirmationModal
        title="Close Final Decisions?"
        description="Are you sure you want to stop working on Final Decisions? You can either Save and Close to pick up where you left off later, or Cancel to stay and continue working right now."
        confirmButtonText="Save and Close"
        cancelButtonText="Cancel"
        open={isConfirmationDialogOpen}
        onAccept={handleConfirmationDialogSave}
        onCancel={() => {
          setConfirmationDialogOpen(false);
        }}
        loading={patchFormDraftMutation.isPending}
      />

      <ConfirmationModal
        title="You are leaving the page"
        description="Are you sure you want to leave this page? You can either Save and Leave to pick up where you left off later, or Cancel to stay and continue working right now."
        confirmButtonText="Save and Leave"
        cancelButtonText="Cancel"
        open={showPrompt}
        onAccept={handleConfirmationDialogSave}
        onCancel={() => cancelNavigation()}
        loading={patchFormDraftMutation.isPending}
      />
    </>
  );
}
